import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Clock, User, DollarSign, Wrench, CheckCircle, XCircle, 
  AlertCircle, Bot, Mail, Image as ImageIcon, Calendar 
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function JobDetailModal({ open, onClose, job, onSuccess, currentUser }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  
  const [assignData, setAssignData] = useState({
    contractor_name: '',
    contractor_phone: '',
    status: 'assigned'
  });
  
  const [declineData, setDeclineData] = useState({
    decline_reason: '',
    landlord_max_budget: '',
    decline_notes: ''
  });
  
  const [approveData, setApproveData] = useState({
    estimated_cost: '',
    pm_notes: '',
    owner_notes: '',
    tenant_notes: '',
    trade_type: job?.trade_type || 'general'
  });

  useEffect(() => {
    if (open && job) {
      loadTimeline();
    }
  }, [open, job]);

  const loadTimeline = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/maintenance-jobs/${job.id}/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeline(response.data);
    } catch (error) {
      console.error('Failed to load timeline:', error);
    }
  };

  const handleAssignContractor = async () => {
    if (!assignData.contractor_name || !assignData.contractor_phone) {
      toast.error('Please enter tradie name and phone');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/maintenance-jobs/${job.id}`,
        {
          assigned_contractor_name: assignData.contractor_name,
          assigned_contractor_phone: assignData.contractor_phone,
          status: 'assigned'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Tradie assigned! Tenant will be notified with tradie details.');
      setShowAssignForm(false);
      setAssignData({ contractor_name: '', contractor_phone: '', status: 'assigned' });
      onSuccess();
      loadTimeline();
    } catch (error) {
      toast.error('Failed to assign tradie');
    } finally {
      setLoading(false);
    }
  };

  const handlePMApprove = async () => {
    if (!approveData.estimated_cost) {
      toast.error('Please enter estimated cost');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/maintenance-jobs/${job.id}/pm-approve`,
        {
          estimated_cost: parseFloat(approveData.estimated_cost),
          pm_notes: approveData.pm_notes,
          owner_notes: approveData.owner_notes,
          tenant_notes: approveData.tenant_notes,
          trade_type: approveData.trade_type
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        parseFloat(approveData.estimated_cost) > 500
          ? 'Job approved! Landlord will be notified for approval.'
          : 'Job approved! You can now assign a contractor.'
      );
      setShowApproveForm(false);
      onSuccess();
      loadTimeline();
    } catch (error) {
      toast.error('Failed to approve job');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/maintenance-jobs/${job.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Job status updated to ${newStatus}`);
      onSuccess();
      loadTimeline();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleLandlordApprove = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/maintenance-jobs/${job.id}/owner-approve`,
        { approved: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Job approved! Property manager can now proceed.');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to approve job');
    } finally {
      setLoading(false);
    }
  };

  const handleLandlordDecline = async () => {
    if (!declineData.decline_reason) {
      toast.error('Please select a decline reason');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        approved: false,
        decline_reason: declineData.decline_reason,
        decline_notes: declineData.decline_notes || null
      };

      // Add budget only if cost_too_high
      if (declineData.decline_reason === 'cost_too_high' && declineData.landlord_max_budget) {
        payload.landlord_max_budget = parseFloat(declineData.landlord_max_budget);
      }

      const response = await axios.post(
        `${API}/maintenance-jobs/${job.id}/owner-approve`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'pm_review_required') {
        toast.success('Your property manager will find a more cost-effective solution.');
      } else {
        toast.success('Job declined and closed.');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to decline job');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'created': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'assigned': return <User className="w-4 h-4 text-green-500" />;
      case 'status_changed': return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case 'email_sent': return <Mail className="w-4 h-4 text-purple-500" />;
      case 'photo_added': return <ImageIcon className="w-4 h-4 text-pink-500" />;
      case 'ai_processed': return <Bot className="w-4 h-4 text-indigo-500" />;
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatActivityDetails = (metadata) => {
    if (!metadata || Object.keys(metadata).length === 0) return null;

    const details = [];
    
    // Format different types of metadata
    if (metadata.contractor_name) {
      details.push(`Tradie: ${metadata.contractor_name}`);
    }
    if (metadata.contractor_phone) {
      details.push(`Phone: ${metadata.contractor_phone}`);
    }
    if (metadata.old_status && metadata.new_status) {
      details.push(`Status changed from "${metadata.old_status}" to "${metadata.new_status}"`);
    }
    if (metadata.estimated_cost) {
      details.push(`Estimated cost: $${metadata.estimated_cost}`);
    }
    if (metadata.email_to) {
      details.push(`Sent to: ${metadata.email_to}`);
    }
    if (metadata.email_type) {
      details.push(`Type: ${metadata.email_type.replace('_', ' ')}`);
    }
    if (metadata.classification) {
      details.push(`Classification: ${metadata.classification}`);
    }
    if (metadata.urgency) {
      details.push(`Urgency: ${metadata.urgency}`);
    }
    if (metadata.photo_count) {
      details.push(`${metadata.photo_count} photo(s) uploaded`);
    }
    if (metadata.decline_reason) {
      details.push(`Reason: ${metadata.decline_reason.replace('_', ' ')}`);
    }
    if (metadata.landlord_max_budget) {
      details.push(`Max budget: $${metadata.landlord_max_budget}`);
    }
    if (metadata.owner_notes) {
      details.push(`Notes: ${metadata.owner_notes}`);
    }

    return details.length > 0 ? details : null;
  };

  if (!job) return null;

  const isPM = currentUser?.role === 'agent' || currentUser?.role === 'admin';
  const isLandlord = currentUser?.role === 'landlord';
  const isTenant = currentUser?.role === 'tenant';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{job.title}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Job Details */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-3">Job Details</h3>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-gray-500">Description</Label>
                  <p className="text-gray-900 mt-1">{job.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-gray-500">Severity</Label>
                    <p className={`mt-1 inline-block px-2 py-1 text-xs rounded font-medium ${
                      job.severity === 'emergency' ? 'bg-red-100 text-red-800' :
                      job.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                      job.severity === 'medium' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {job.severity}
                    </p>
                  </div>

                  <div>
                    <Label className="text-sm text-gray-500">Status</Label>
                    <p className={`mt-1 inline-block px-2 py-1 text-xs rounded font-medium ${
                      job.status === 'completed' ? 'bg-green-100 text-green-800' :
                      job.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      job.status === 'assigned' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {job.status.replace('_', ' ')}
                    </p>
                  </div>
                </div>

                {/* AI Classification */}
                {job.trade_type && (
                  <div>
                    <Label className="text-sm text-gray-500 flex items-center gap-1">
                      <Bot className="w-3 h-3" />
                      AI Classification
                    </Label>
                    <div className="mt-1 bg-purple-50 border border-purple-200 rounded p-2">
                      <p className="text-purple-900 font-medium capitalize">{job.trade_type}</p>
                      <p className="text-purple-700 text-xs">Automatically classified by AI</p>
                    </div>
                  </div>
                )}

                {job.assigned_contractor_name && (
                  <div>
                    <Label className="text-sm text-gray-500">Assigned Tradie</Label>
                    <div className="mt-1 bg-green-50 border border-green-200 rounded p-2">
                      <p className="text-green-900 font-medium">{job.assigned_contractor_name}</p>
                      <p className="text-green-700 text-sm">{job.assigned_contractor_phone}</p>
                    </div>
                  </div>
                )}

                {job.estimated_cost && (
                  <div>
                    <Label className="text-sm text-gray-500">Estimated Cost</Label>
                    <p className="text-gray-900 mt-1 font-semibold">${job.estimated_cost.toFixed(2)}</p>
                  </div>
                )}

                {job.photos && job.photos.length > 0 && (
                  <div>
                    <Label className="text-sm text-gray-500 mb-2 block">Photos</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {job.photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={`${API_URL}${photo}`}
                          alt={`Job photo ${idx + 1}`}
                          className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-80"
                          onClick={() => window.open(`${API_URL}${photo}`, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* PM Actions */}
            {isPM && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Property Manager Actions</h3>
                
                {job.status === 'pending' && !showApproveForm && (
                  <Button onClick={() => setShowApproveForm(true)} className="w-full mb-2">
                    Review & Approve Job
                  </Button>
                )}

                {showApproveForm && (
                  <div className="space-y-3 p-4 bg-gray-50 rounded">
                    <div>
                      <Label>Estimated Cost *</Label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={approveData.estimated_cost}
                        onChange={(e) => setApproveData({...approveData, estimated_cost: e.target.value})}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Jobs over $500 require landlord approval
                      </p>
                    </div>

                    <div>
                      <Label>Trade Type</Label>
                      <Select value={approveData.trade_type} onValueChange={(v) => setApproveData({...approveData, trade_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="plumber">Plumber</SelectItem>
                          <SelectItem value="electrician">Electrician</SelectItem>
                          <SelectItem value="carpenter">Carpenter</SelectItem>
                          <SelectItem value="cleaner">Cleaner</SelectItem>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Notes for Landlord</Label>
                      <Textarea
                        placeholder="What the landlord will see..."
                        value={approveData.owner_notes}
                        onChange={(e) => setApproveData({...approveData, owner_notes: e.target.value})}
                        rows={2}
                      />
                    </div>

                    <div>
                      <Label>Notes for Tenant</Label>
                      <Textarea
                        placeholder="What the tenant will see..."
                        value={approveData.tenant_notes}
                        onChange={(e) => setApproveData({...approveData, tenant_notes: e.target.value})}
                        rows={2}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={handlePMApprove} disabled={loading} className="flex-1">
                        {loading ? 'Approving...' : 'Approve Job'}
                      </Button>
                      <Button variant="outline" onClick={() => setShowApproveForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {(job.status === 'approved' || job.status === 'assigned') && !showAssignForm && (
                  <Button onClick={() => setShowAssignForm(true)} className="w-full mb-2">
                    {job.assigned_contractor_name ? 'Change Tradie' : 'Assign Tradie'}
                  </Button>
                )}

                {showAssignForm && (
                  <div className="space-y-3 p-4 bg-gray-50 rounded">
                    <div>
                      <Label>Tradie Name *</Label>
                      <Input
                        placeholder="e.g., John's Plumbing"
                        value={assignData.contractor_name || ''}
                        onChange={(e) => setAssignData({...assignData, contractor_name: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Tradie Phone *</Label>
                      <Input
                        placeholder="e.g., 0412 345 678"
                        value={assignData.contractor_phone || ''}
                        onChange={(e) => setAssignData({...assignData, contractor_phone: e.target.value})}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={handleAssignContractor} disabled={loading} className="flex-1">
                        {loading ? 'Assigning...' : 'Assign & Notify Tenant'}
                      </Button>
                      <Button variant="outline" onClick={() => setShowAssignForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {job.status === 'assigned' && (
                  <Button onClick={() => handleStatusChange('in_progress')} variant="outline" className="w-full mb-2">
                    Mark as In Progress
                  </Button>
                )}

                {job.status === 'in_progress' && (
                  <Button onClick={() => handleStatusChange('completed')} className="w-full mb-2">
                    Mark as Completed
                  </Button>
                )}
              </div>
            )}

            {/* Landlord Approval */}
            {isLandlord && job.requires_owner_approval && !job.owner_approved && !showDeclineForm && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3 text-red-600">Approval Required</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Estimated cost: <strong>${job.estimated_cost?.toFixed(2)}</strong>
                </p>
                {job.owner_visible_notes && (
                  <div className="bg-gray-50 rounded p-3 mb-3">
                    <p className="text-sm text-gray-700">{job.owner_visible_notes}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={handleLandlordApprove} disabled={loading} className="flex-1">
                    {loading ? 'Processing...' : 'Approve'}
                  </Button>
                  <Button variant="destructive" onClick={() => setShowDeclineForm(true)} className="flex-1">
                    Decline
                  </Button>
                </div>
              </div>
            )}

            {/* Landlord Decline Form */}
            {isLandlord && showDeclineForm && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3 text-red-600">Decline Maintenance Request</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label>Reason for Declining *</Label>
                    <select
                      value={declineData.decline_reason}
                      onChange={(e) => setDeclineData({...declineData, decline_reason: e.target.value})}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select reason...</option>
                      <option value="cost_too_high">Cost too high</option>
                      <option value="not_necessary">Not necessary</option>
                      <option value="tenant_responsibility">Tenant's responsibility</option>
                      <option value="will_handle_myself">I'll handle myself</option>
                      <option value="property_being_sold">Property being sold</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {declineData.decline_reason === 'cost_too_high' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                      <p className="text-sm text-yellow-800 mb-3">
                        If the cost is your concern, help your property manager find a cheaper solution:
                      </p>
                      <div>
                        <Label>What's your maximum budget?</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 500"
                          value={declineData.landlord_max_budget}
                          onChange={(e) => setDeclineData({...declineData, landlord_max_budget: e.target.value})}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Optional but helpful</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Additional Notes</Label>
                    <Textarea
                      placeholder="Any additional information..."
                      value={declineData.decline_notes}
                      onChange={(e) => setDeclineData({...declineData, decline_notes: e.target.value})}
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleLandlordDecline} 
                      disabled={loading || !declineData.decline_reason}
                      variant="destructive"
                      className="flex-1"
                    >
                      {loading ? 'Processing...' : 'Confirm Decline'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowDeclineForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Timeline */}
          <div>
            <h3 className="font-semibold text-lg mb-3">Activity Timeline</h3>
            
            {timeline.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No activity yet</p>
            ) : (
              <div className="space-y-3">
                {timeline.map((activity, idx) => {
                  const details = formatActivityDetails(activity.metadata);
                  
                  return (
                    <div key={activity.id} className="flex gap-3 pb-3 border-b last:border-0">
                      <div className="mt-1">
                        {getActivityIcon(activity.activity_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{activity.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {format(new Date(activity.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                        {details && details.length > 0 && (
                          <div className="mt-2 bg-gray-50 rounded px-2 py-1.5 space-y-0.5">
                            {details.map((detail, i) => (
                              <p key={i} className="text-xs text-gray-600">• {detail}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
